import React, { useState, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box, Popover, Typography, List, ListItem } from '@mui/material';
import { copyToClipboard } from '@tenant/utility/helper/Utils';

// Styled components
const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
}));

const PrimaryItem = styled(Typography)(({ theme }) => ({
  marginRight: 8,
  fontSize: 'inherit',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: 'calc(100% - 24px)',
}));

const MoreIndicator = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: 12,
  cursor: 'pointer',
  color: theme.palette.background.paper,
  padding: 2,
  borderRadius: 4,
}));

const ResultPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
    padding: theme.spacing(1),
    minWidth: 150,
    maxWidth: 300,
  },
}));

const ResultItem = styled(ListItem)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 14,
  padding: theme.spacing(1, 2),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

/**
 * AppExpandResult Component
 * Displays a primary item with an expandable popup for additional items
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {number} props.maxVisibleItems - Maximum number of items to show before collapsing
 * @param {function} props.onItemClick - Callback function when an item is clicked
 * @returns {JSX.Element}
 */
const AppExpandResult = ({
  items = [],
  maxVisibleItems = 1,
  onItemClick = copyToClipboard,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const containerRef = useRef(null);

  const additionalItemsCount = Math.max(0, items.length - maxVisibleItems);
  const visibleItems = items.slice(0, maxVisibleItems);

  const handleClick = (event) => {
    if (additionalItemsCount > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item);
    }
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <MainContainer ref={containerRef}>
      {visibleItems.map((item) => (
        <PrimaryItem
          key={item}
          title={item}
          onClick={() => onItemClick?.(item)}
        >
          {item}
        </PrimaryItem>
      ))}

      {additionalItemsCount > 0 && (
        <MoreIndicator onClick={handleClick}>
          +{additionalItemsCount}
        </MoreIndicator>
      )}

      <ResultPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <List>
          {items.map((item) => (
            <ResultItem key={item} onClick={() => handleItemClick(item)}>
              <Box component='span' title={item}>
                {item}
              </Box>
            </ResultItem>
          ))}
        </List>
      </ResultPopover>
    </MainContainer>
  );
};

AppExpandResult.propTypes = {
  items: PropTypes.array,
  maxVisibleItems: PropTypes.number,
  onItemClick: PropTypes.func,
};

export default memo(AppExpandResult);
